/**
 * Every state a run can be in, said plainly.
 *
 * THE RULE THIS COMPONENT EXISTS FOR: a spinner is a claim that work is
 * happening. When the worker is dead, the spinner is a lie, and the user waits
 * on it indefinitely because nothing else on screen contradicts it. So
 * `timed_out` is a first-class visible state with an action, not an edge case,
 * and it is reached by the READ-TIME resolver rather than by anything the dead
 * worker managed to write.
 *
 * `insufficient_evidence` is likewise a RESULT, not a failure. It means the
 * search ran and the evidence did not support publication — which is the honest
 * answer, and the product's whole point. It reads differently from `failed`.
 */

import React from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileSearch,
  RefreshCw,
  ScanSearch,
  ShieldCheck,
} from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { messageFor } from '@/lib/strategic-analysis/failure-messages';
import type { ResolvedRunState } from '@/lib/strategic-analysis/status';

export interface AnalysisStatusCardProps {
  state: ResolvedRunState;
  failureCode?: string | null;
  /** Only an active startup member may retry or generate. */
  canGenerate?: boolean;
  onGenerate?: () => void;
  /** A previously ready report is on screen while this run works. */
  showingPrevious?: boolean;
  /** Ready, but built from inputs that have since been superseded. */
  isStale?: boolean;
  onRefresh?: () => void;
}

const WORKING: ReadonlySet<ResolvedRunState> = new Set<ResolvedRunState>([
  'queued',
  'researching',
  'synthesizing',
  'validating',
]);

/** One sentence per state, in the interface's voice. No apologies, no jargon. */
const HEADLINE: Record<ResolvedRunState, string> = {
  not_generated: 'Not generated yet',
  queued: 'Queued',
  researching: 'Researching sources',
  synthesizing: 'Drafting the analysis',
  validating: 'Checking every claim against its sources',
  ready: 'Ready',
  insufficient_evidence: 'Not enough evidence to publish',
  failed: 'This analysis could not be completed',
  timed_out: 'This analysis was stopped',
};

const DETAIL: Partial<Record<ResolvedRunState, string>> = {
  not_generated: 'Run it when your analysis inputs are ready.',
  queued: 'Waiting to start.',
  researching: 'Searching for sources and for evidence that would weaken the conclusions.',
  synthesizing: 'Turning retained evidence into findings.',
  validating: 'A finding without supporting evidence does not get published.',
  insufficient_evidence:
    'The research ran, but the evidence did not support enough of the analysis to publish it. What was searched is shown under How this was derived.',
  timed_out: 'It ran past the time limit and was stopped. You can start it again.',
};

export function AnalysisStatusCard({
  state,
  failureCode,
  canGenerate = false,
  onGenerate,
  showingPrevious = false,
  isStale = false,
  onRefresh,
}: AnalysisStatusCardProps) {
  const colors = useThemeColors();
  const working = WORKING.has(state);

  const tone = working
    ? { bg: colors.primary[50], fg: colors.primary[700], border: colors.primary[100] }
    : state === 'ready'
    ? { bg: colors.traffic.greenLight, fg: colors.success, border: colors.traffic.greenLight }
    : state === 'insufficient_evidence'
    ? { bg: colors.amber.light, fg: colors.amber.main, border: colors.amber.light }
    : state === 'not_generated'
    ? { bg: colors.surface.secondary, fg: colors.text.secondary, border: colors.border.light }
    : { bg: colors.traffic.redLight, fg: colors.error, border: colors.traffic.redLight };

  const Icon = working
    ? state === 'researching'
      ? ScanSearch
      : state === 'validating'
      ? ShieldCheck
      : state === 'synthesizing'
      ? FileSearch
      : Clock
    : state === 'ready'
    ? CheckCircle2
    : state === 'not_generated'
    ? FileSearch
    : AlertTriangle;

  // A worker that reached its own error path knows more than a generic label
  // does; use its safe code when it left one.
  const detail = state === 'failed' && failureCode ? messageFor(failureCode) : DETAIL[state];

  const action = state === 'not_generated'
    ? 'Generate analysis'
    : state === 'timed_out' || state === 'failed'
    ? 'Try again'
    : state === 'insufficient_evidence'
    ? 'Run again'
    : null;

  return (
    <View
      accessibilityRole="summary"
      accessibilityLabel={`Analysis status: ${HEADLINE[state]}`}
      className="rounded-2xl p-4 gap-3"
      style={{ backgroundColor: tone.bg, borderWidth: 1, borderColor: tone.border, overflow: 'hidden' }}
    >
      <View className="flex-row items-center gap-2.5">
        {working
          ? <ActivityIndicator size="small" color={tone.fg} />
          : <Icon size={18} color={tone.fg} />}
        {/* Two lines: "Not enough evidence to publish" does not fit one at
            390pt beside the stale badge, and truncating a state name to
            "Not enough evidence to …" loses the half that says what happened. */}
        <Text numberOfLines={2} className="flex-1 text-base font-semibold leading-5" style={{ color: tone.fg }}>
          {HEADLINE[state]}
        </Text>
        {isStale && (
          <View className="rounded-full px-2 py-1 shrink-0" style={{ backgroundColor: colors.amber.light }}>
            <Text numberOfLines={1} className="text-xs font-semibold" style={{ color: colors.amber.main }}>
              Inputs changed
            </Text>
          </View>
        )}
      </View>

      {detail && (
        <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
          {detail}
        </Text>
      )}

      {isStale && (
        <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
          This analysis used an earlier version of your inputs. It is still a true record of what was
          assessed. Refresh to reassess with the current version.
        </Text>
      )}

      {showingPrevious && (
        <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
          Showing the previous analysis while this one runs.
        </Text>
      )}

      {/* Generation and refresh belong to active startup members only. An
          investor sees the state and never an action that would spend a credit
          against someone else's account. */}
      {canGenerate && action && onGenerate && (
        <View className="rounded-xl overflow-hidden" style={{ height: 44, backgroundColor: colors.primary[600] }}>
          <AnimatedPressable
            onPress={onGenerate}
            accessibilityLabel={action}
            className="flex-row items-center justify-center gap-2"
            style={{ flex: 1 }}
          >
            <Text numberOfLines={1} className="text-sm font-semibold" style={{ color: colors.text.inverse }}>
              {action}
            </Text>
          </AnimatedPressable>
        </View>
      )}

      {canGenerate && isStale && onRefresh && (
        <View
          className="rounded-xl overflow-hidden"
          style={{ height: 44, backgroundColor: colors.surface.primary }}
        >
          <AnimatedPressable
            onPress={onRefresh}
            accessibilityLabel="Refresh analysis"
            className="flex-row items-center justify-center gap-2"
            style={{ flex: 1 }}
          >
            <RefreshCw size={15} color={colors.primary[700]} />
            <Text numberOfLines={1} className="text-sm font-semibold" style={{ color: colors.primary[700] }}>
              Refresh analysis
            </Text>
          </AnimatedPressable>
        </View>
      )}
    </View>
  );
}
